import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import {
  listKnowledgeItems,
  approveKnowledgeItem,
  rejectKnowledgeItem,
} from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}>
    {children}
  </div>
);

const JURIS_STYLES = {
  US: "bg-sky-500/15 text-sky-500",
  IN: "bg-amber-500/15 text-amber-500",
  GLOBAL: "bg-gray-500/15 text-gray-400",
};

// Real, clickable source vs a placeholder/empty one we shouldn't link.
const isRealUrl = (u) =>
  !!u && /^https?:\/\//i.test(u) && !/(^|\.)example\.(com|org|net|gov\.in)\b/i.test(u);

const ItemCard = ({ item, onDecide, busy }) => (
  <Card>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400">
            {item.agent_name || item.agent_key}
          </span>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${JURIS_STYLES[item.jurisdiction] || JURIS_STYLES.GLOBAL}`}>
            {item.jurisdiction}
          </span>
          <span className="text-[11px] text-gray-500">
            conf {item.confidence}% · eff {item.effective_date || "current"} · via {item.source_type}
          </span>
        </div>
        <h3 className="font-bold leading-snug">{item.title}</h3>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          disabled={busy}
          onClick={() => onDecide(item.id, "approve")}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50"
        >
          <CheckIcon className="w-4 h-4" /> Approve
        </button>
        <button
          disabled={busy}
          onClick={() => onDecide(item.id, "reject")}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-sm font-semibold disabled:opacity-50"
        >
          <XMarkIcon className="w-4 h-4" /> Reject
        </button>
      </div>
    </div>

    {item.summary && <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{item.summary}</p>}

    {item.body && (
      <div className="mt-3 p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10">
        <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">Guidance the specialist will use</p>
        <p className="text-sm whitespace-pre-wrap">{item.body}</p>
      </div>
    )}

    {/* Diff vs current rule it would replace */}
    {item.supersedes_title && (
      <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">
        Replaces current active rule: “{item.supersedes_title}”
      </p>
    )}

    {isRealUrl(item.source_url) ? (
      <a
        href={item.source_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:underline mt-2"
      >
        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        {item.source_title || item.source_url}
      </a>
    ) : (item.source_title || item.source_url) ? (
      <p className="text-xs text-gray-500 mt-2">
        Source: {item.source_title || item.source_url}
        {item.source_url ? " (not a live link)" : ""}
      </p>
    ) : null}
  </Card>
);

const ResearchApprovals = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const data = await listKnowledgeItems({ status: "proposed" });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDecide = async (id, decision) => {
    setBusyId(id);
    // optimistic removal
    const prev = items;
    setItems((list) => list.filter((it) => it.id !== id));
    try {
      if (decision === "approve") await approveKnowledgeItem(id);
      else await rejectKnowledgeItem(id);
      setMsg(decision === "approve" ? "Approved — now live to the specialists." : "Rejected.");
      setTimeout(() => setMsg(""), 2500);
    } catch {
      setItems(prev); // rollback
      setMsg("Action failed. Try again.");
      setTimeout(() => setMsg(""), 2500);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/research" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-500 mb-1">
            <ArrowLeftIcon className="w-4 h-4" /> Research Desk
          </Link>
          <h1 className="text-3xl font-bold">Review queue</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Approve to push knowledge live to the Council; reject to discard. Nothing
            reaches the specialists until you approve it.
          </p>
        </div>
      </div>

      {msg && (
        <div className="p-3 rounded-xl bg-indigo-900/20 border border-indigo-700 text-indigo-200 text-sm">{msg}</div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">
            Nothing to review. Run a desk from the{" "}
            <Link to="/admin/research" className="text-indigo-500 hover:underline">Research Desk</Link>{" "}
            to propose new knowledge.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <ItemCard key={it.id} item={it} onDecide={handleDecide} busy={busyId === it.id} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResearchApprovals;
